import { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload, FileText, ClipboardPaste, CheckCircle2, AlertTriangle,
  XCircle, Download, ArrowRight, Loader2,
} from "lucide-react";

export default function ImportExportPanel({ open, onOpenChange, groups, onImportDone }) {
  const [tab, setTab] = useState("import");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="import-export-dialog">
        <DialogHeader>
          <DialogTitle data-testid="import-export-title">Import & Export</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="import" className="flex-1" data-testid="tab-import">
              <Upload size={14} className="mr-1.5" /> Import
            </TabsTrigger>
            <TabsTrigger value="export" className="flex-1" data-testid="tab-export">
              <Download size={14} className="mr-1.5" /> Export
            </TabsTrigger>
          </TabsList>

          <TabsContent value="import">
            <ImportTab groups={groups} onDone={() => { onImportDone(); onOpenChange(false); }} />
          </TabsContent>

          <TabsContent value="export">
            <ExportTab groups={groups} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}


// ──────────────────────────────────────────
// IMPORT TAB
// ──────────────────────────────────────────

function ImportTab({ groups, onDone }) {
  const [step, setStep] = useState("upload"); // upload | preview | result
  const [session, setSession] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("skip");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUploadComplete = (data) => {
    setSession(data);
    loadPreview(data.session_id);
  };

  const loadPreview = async (sessionId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts-io/preview/${sessionId}`);
      setPreview(data);
      setStep("preview");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/contacts-io/commit/${session.session_id}`, { mode });
      setResult(data.result);
      setStep("result");
      toast.success(`Import complete: ${data.result.created} created, ${data.result.updated} updated`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === "result" && result) {
    return (
      <div className="space-y-4 py-2" data-testid="import-result">
        <div className="text-center py-4">
          <CheckCircle2 size={36} className="mx-auto text-green-500 mb-2" />
          <h3 className="text-sm font-medium text-zinc-800">Import complete</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ResultCard label="Created" value={result.created} color="green" />
          <ResultCard label="Updated" value={result.updated} color="blue" />
          <ResultCard label="Skipped" value={result.skipped} color="zinc" />
          <ResultCard label="Errors" value={result.errors} color="red" />
        </div>
        <Button className="w-full" size="sm" onClick={onDone} data-testid="import-done-btn">
          Done
        </Button>
      </div>
    );
  }

  if (step === "preview" && preview) {
    return (
      <div className="space-y-4 py-2" data-testid="import-preview">
        {/* Summary */}
        <div className="grid grid-cols-4 gap-2">
          <SummaryCard label="Total" value={preview.summary.total} />
          <SummaryCard label="New" value={preview.summary.valid} color="green" />
          <SummaryCard label="Duplicates" value={preview.summary.duplicates} color="amber" />
          <SummaryCard label="Invalid" value={preview.summary.invalid} color="red" />
        </div>

        {/* Row preview */}
        <div className="border rounded-md max-h-44 overflow-y-auto divide-y divide-zinc-100" data-testid="import-rows-preview">
          {preview.rows.slice(0, 50).map((row) => (
            <div key={row.row_id} className="px-3 py-1.5 flex items-center gap-2 text-xs">
              <StatusIcon status={row.status} />
              <span className="font-mono text-zinc-700 truncate flex-1">{row.email}</span>
              {row.name && <span className="text-zinc-500 truncate max-w-[120px]">{row.name}</span>}
              {row.group_name && (
                <Badge variant="outline" className="text-[9px] px-1 py-0">{row.group_name}</Badge>
              )}
              {row.error_message && (
                <span className="text-red-500 text-[10px]">{row.error_message}</span>
              )}
            </div>
          ))}
        </div>

        {/* Merge mode */}
        {preview.summary.duplicates > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-zinc-600">
              {preview.summary.duplicates} duplicate(s) found. How to handle?
            </p>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-9" data-testid="merge-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Skip duplicates (keep existing)</SelectItem>
                <SelectItem value="overwrite">Overwrite existing contacts</SelectItem>
                <SelectItem value="merge">Merge (fill missing fields only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setStep("upload"); setSession(null); setPreview(null); }}>
            Back
          </Button>
          <Button
            size="sm"
            className="flex-1"
            disabled={loading || preview.summary.valid + preview.summary.duplicates === 0}
            onClick={handleCommit}
            data-testid="import-commit-btn"
          >
            {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ArrowRight size={14} className="mr-1.5" />}
            Import {preview.summary.valid + (mode !== "skip" ? preview.summary.duplicates : 0)} contact(s)
          </Button>
        </div>
      </div>
    );
  }

  // Upload step
  return (
    <div className="space-y-4 py-2">
      <UploadSection onComplete={handleUploadComplete} loading={loading} setLoading={setLoading} />
      <div className="relative">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-zinc-200" /></div>
        <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-zinc-500">or</span></div>
      </div>
      <PasteSection onComplete={handleUploadComplete} loading={loading} setLoading={setLoading} />
    </div>
  );
}


// ──────────────────────────────────────────
// UPLOAD SECTION
// ──────────────────────────────────────────

function UploadSection({ onComplete, loading, setLoading }) {
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/contacts-io/upload-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onComplete(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      className="border-2 border-dashed border-zinc-200 rounded-lg p-5 text-center hover:border-zinc-300 transition-colors cursor-pointer"
      onClick={() => fileRef.current?.click()}
      data-testid="csv-upload-area"
    >
      <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} className="hidden" data-testid="csv-file-input" />
      <FileText size={28} className="mx-auto text-zinc-300 mb-2" />
      <p className="text-sm font-medium text-zinc-600">Upload CSV file</p>
      <p className="text-[10px] text-zinc-500 mt-0.5">
        Columns: email (required), name, group, tags
      </p>
      {loading && <Loader2 size={16} className="mx-auto mt-2 animate-spin text-blue-500" />}
    </div>
  );
}


// ──────────────────────────────────────────
// PASTE SECTION
// ──────────────────────────────────────────

function PasteSection({ onComplete, loading, setLoading }) {
  const [text, setText] = useState("");

  const handlePaste = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/contacts-io/paste", { text });
      onComplete(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="paste-section">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"Paste email addresses (one per line)\n\nalice@example.com\nBob <bob@test.com>\nCharlie charlie@work.org"}
        rows={4}
        className="text-sm font-mono"
        data-testid="paste-textarea"
      />
      <Button
        size="sm"
        variant="outline"
        disabled={!text.trim() || loading}
        onClick={handlePaste}
        data-testid="paste-import-btn"
      >
        <ClipboardPaste size={14} className="mr-1.5" />
        Parse contacts
      </Button>
    </div>
  );
}


// ──────────────────────────────────────────
// EXPORT TAB
// ──────────────────────────────────────────

function ExportTab({ groups }) {
  const [groupFilter, setGroupFilter] = useState("all");
  const [visFilter, setVisFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (groupFilter !== "all") params.set("group_id", groupFilter);
      if (visFilter !== "all") params.set("visibility", visFilter);

      const response = await api.get(`/contacts-io/export?${params}`, {
        responseType: "blob",
      });

      const count = response.headers["x-export-count"] || "?";
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contacts_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${count} contacts`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 py-2" data-testid="export-tab">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-zinc-600">Filter by group</p>
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="h-9" data-testid="export-group-filter">
              <SelectValue placeholder="All contacts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All contacts</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-zinc-600">Filter by visibility</p>
          <Select value={visFilter} onValueChange={setVisFilter}>
            <SelectTrigger className="h-9" data-testid="export-visibility-filter">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="only_me">Private only</SelectItem>
              <SelectItem value="all_senders">All senders</SelectItem>
              <SelectItem value="specific_senders">Specific senders</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button size="sm" onClick={handleExport} disabled={loading} className="w-full" data-testid="export-btn">
        {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
        Export as CSV
      </Button>
    </div>
  );
}


// ──────────────────────────────────────────
// SMALL COMPONENTS
// ──────────────────────────────────────────

function StatusIcon({ status }) {
  if (status === "valid") return <CheckCircle2 size={13} className="text-green-500 shrink-0" />;
  if (status === "duplicate") return <AlertTriangle size={13} className="text-amber-500 shrink-0" />;
  return <XCircle size={13} className="text-red-400 shrink-0" />;
}

function SummaryCard({ label, value, color = "zinc" }) {
  const colors = {
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-600",
    zinc: "bg-zinc-50 text-zinc-700",
  };
  return (
    <div className={`rounded-md px-3 py-2 text-center ${colors[color] || colors.zinc}`}>
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-[10px]">{label}</p>
    </div>
  );
}

function ResultCard({ label, value, color }) {
  const colors = {
    green: "border-green-200 text-green-700",
    blue: "border-blue-200 text-blue-700",
    zinc: "border-zinc-200 text-zinc-500",
    red: "border-red-200 text-red-600",
  };
  return (
    <div className={`border rounded-md px-3 py-2 text-center ${colors[color]}`}>
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-[10px]">{label}</p>
    </div>
  );
}
